import { useEffect, useState } from 'react';
import { Mail, Send, Loader2 } from 'lucide-react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const STATUS_LABEL = { sent: 'enviado', mock: 'simulado (email no configurado)', error: 'con error' };

function formatDate(iso) {
  return iso ? new Date(iso).toLocaleString('es-PY', { dateStyle: 'medium', timeStyle: 'short' }) : null;
}

export function QrWeeklyReport() {
  const [settings, setSettings] = useState(null);
  const [recipient, setRecipient] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/admin/qr/weekly-report')
      .then((res) => { setSettings(res.data); setRecipient(res.data.recipient); })
      .catch(() => toast.error('No se pudo cargar la configuración del resumen semanal'));
  }, []);

  const save = async (fields) => {
    setSaving(true);
    try {
      const res = await api.put('/admin/qr/weekly-report', fields);
      setSettings(res.data);
      setRecipient(res.data.recipient);
      toast.success('Configuración guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
    finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/qr/weekly-report/send');
      toast.success(res.data.status === 'mock' ? 'Email simulado: el servicio de email no está configurado' : `Resumen enviado a ${res.data.to}`);
      setSettings((s) => ({ ...s, last_sent_at: new Date().toISOString(), last_status: res.data.status }));
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar el resumen'); }
    finally { setSending(false); }
  };

  const dirty = settings && recipient.trim() !== settings.recipient;
  return (
    <section className="mt-8 bg-white rounded-xl border border-[#E7E5E4] p-5" data-testid="qr-weekly-report">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="w-10 h-10 rounded-lg bg-[#0D1D2A] text-[#E7B72D] flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></span>
          <div>
            <h2 className="text-base font-semibold text-[#1C1917]" style={{ fontFamily: 'Outfit' }}>Resumen semanal por email</h2>
            <p className="text-xs text-[#78716C] mt-0.5">
              Cada lunes a las 09:00 (Asunción) con los escaneos de la semana, visitantes únicos y el botón más pulsado.
              {settings?.last_sent_at && <> · Último envío: {formatDate(settings.last_sent_at)} ({STATUS_LABEL[settings.last_status] || settings.last_status})</>}
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#57534E]">
          {settings?.enabled ? 'Activado' : 'Desactivado'}
          <Switch checked={Boolean(settings?.enabled)} disabled={!settings || saving} onCheckedChange={(v) => save({ enabled: v })} data-testid="qr-weekly-toggle" />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="admin@estadia365.com" className="max-w-xs" data-testid="qr-weekly-recipient" />
        <Button size="sm" variant="outline" className="border-[#E7E5E4]" disabled={!dirty || saving} onClick={() => save({ recipient: recipient.trim() })} data-testid="qr-weekly-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar destinatario'}
        </Button>
        <Button size="sm" className="bg-[#264639] hover:bg-[#1C352B] ml-auto" disabled={!settings || sending} onClick={sendNow} data-testid="qr-weekly-send-btn">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Enviar ahora
        </Button>
      </div>
    </section>
  );
}
